import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useBackClose } from "../back.js";
import { useText } from "../i18n.js";
import { usePresence } from "../presence.js";
import { DIALOGS } from "../text/app.js";

/** A yes-or-no to ask before doing something that cannot be taken back. */
export interface ConfirmRequest {
  readonly title: string;
  /** What happens, and what does not, in a sentence or two. */
  readonly message?: string;
  /** The button that says yes: "Delete", "Restart". */
  readonly action: string;
  /** Destroys something: the yes is drawn as a warning, and the no has the keyboard. */
  readonly danger?: boolean;
}

export type Confirm = (request: ConfirmRequest) => Promise<boolean>;

/** How long the dialog takes to leave; the modal's CSS exit animation is this long. */
const EXIT_MS = 220;

/** Outside the app (a test, a page of its own), the browser's own box still asks. */
const ConfirmContext = createContext<Confirm>(async (request) => window.confirm(request.message == null ? request.title : `${request.title}\n\n${request.message}`));

/** Asks the user yes or no in the app's own dialog, rather than the browser's confirm box; resolves true for yes. */
export function useConfirm(): Confirm {
  return useContext(ConfirmContext);
}

export const ConfirmProvider = ConfirmContext.Provider;

/**
 * The one confirm dialog of the page, for whoever renders the provider: the
 * function that asks, and the dialog to draw. A second question while one is
 * open answers the first no.
 */
export function useConfirmHost(): { readonly confirm: Confirm; readonly dialog: React.ReactNode } {
  const [asking, setAsking] = useState<ConfirmRequest | null>(null);
  const answer = useRef<((yes: boolean) => void) | null>(null);
  const last = useRef<ConfirmRequest | null>(null);
  if (asking != null) last.current = asking;
  const asked = useRef(0);
  const presence = usePresence(asking != null, EXIT_MS);

  const confirm = useCallback<Confirm>(
    (request) =>
      new Promise<boolean>((resolve) => {
        answer.current?.(false);
        answer.current = resolve;
        asked.current += 1;
        setAsking(request);
      }),
    [],
  );
  const settle = useCallback((yes: boolean): void => {
    const resolve = answer.current;
    answer.current = null;
    setAsking(null);
    resolve?.(yes);
  }, []);

  const dialog = presence.mounted && last.current != null ? createPortal(<ConfirmDialog key={asked.current} request={last.current} closing={presence.closing} onAnswer={settle} />, document.body) : null;
  return { confirm, dialog };
}

function ConfirmDialog({ request, closing, onAnswer }: { readonly request: ConfirmRequest; readonly closing: boolean; readonly onAnswer: (yes: boolean) => void }): React.JSX.Element {
  const t = useText(DIALOGS);
  const titleId = useId();
  const messageId = useId();
  useBackClose(!closing, () => onAnswer(false));
  useEffect(() => {
    // Ahead of anything else listening, so Escape closes this and not the dialog under it.
    const key = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onAnswer(false);
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [onAnswer]);
  const danger = request.danger === true;
  return (
    <div className={`modal-backdrop${closing ? " closing" : ""}`} onClick={() => onAnswer(false)}>
      <div className="modal ask-dialog" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={request.message == null ? undefined : messageId} onClick={(event) => event.stopPropagation()}>
        <h2 id={titleId}>{request.title}</h2>
        {request.message != null ? (
          <p id={messageId} className="ask-dialog-note">
            {request.message}
          </p>
        ) : null}
        <div className="ask-dialog-actions">
          <button type="button" autoFocus={danger} onClick={() => onAnswer(false)}>
            {t.cancel}
          </button>
          <button type="button" autoFocus={!danger} className={danger ? "is-danger" : "primary"} onClick={() => onAnswer(true)}>
            {request.action}
          </button>
        </div>
      </div>
    </div>
  );
}
